import { Card } from '@playerbase/ui';
import type { FormFieldSummary, FormSentimentSummary } from '@playerbase/types';

import { FieldSummaryList } from '../../../feedback/field-summary-list.js';
import { SentimentDot } from '../../../feedback/sentiment-dot.js';

// ---------------------------------------------------------------------------
// Form summary card.
//
// A quantitative read-out of every submission against one form, shown above
// the submission list. The overall sentiment band sits in the header; the
// per-question breakdown is the shared FieldSummaryList (reused by the
// playtest event drill-down).
//
// All copy is plain product language; the data here is derived on read and
// the derivation is never named in the UI.
// ---------------------------------------------------------------------------

interface FormSummaryCardProps {
  summary: FormFieldSummary;
  sentiment: FormSentimentSummary | null;
}

export function FormSummaryCard({ summary, sentiment }: FormSummaryCardProps): JSX.Element {
  return (
    <Card>
      <Card.Content className="flex flex-col gap-5">
        <div className="flex items-center justify-between gap-3">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted">
            Summary
          </span>
          {sentiment !== null && sentiment.band !== null && (
            <span className="inline-flex items-center gap-2">
              <SentimentDot band={sentiment.band} showLabel />
              {sentiment.meanScore !== null && (
                <span className="text-xs tabular-nums text-text-muted">
                  {sentiment.meanScore.toFixed(1)}
                </span>
              )}
            </span>
          )}
        </div>

        <FieldSummaryList fields={summary.fields} />
      </Card.Content>
    </Card>
  );
}
